import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Checkbox, Modal, TextInput } from "@opendate-app/setlist";
import { useSeriesShowContext } from "../SeriesShowApp";
import { fmtCurrencySymbol } from "../passFormatting";

const WHOLE_CENTS = /^(\d+(\.\d{1,2})?|\.\d{1,2})$/;

const SeriesPassSurchargeModal = ({ open, surcharge, onClose }) => {
  const { series, handleCreateSurcharge, handleUpdateSurcharge } = useSeriesShowContext();
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [amountTouched, setAmountTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName(surcharge ? surcharge.name || "" : "");
    setAmount(surcharge && surcharge.default_amount != null ? Number(surcharge.default_amount).toFixed(2) : "");
    setEnabled(surcharge ? Boolean(surcharge.enabled) : true);
    setAmountTouched(false);
    setSubmitting(false);
    setError(null);
  }, [open, surcharge?.id]);

  const amountValid = WHOLE_CENTS.test(amount) && Number(amount) > 0;
  const isValid = name.trim() !== "" && amountValid;

  const submit = async () => {
    if (submitting || !isValid) return;
    try {
      setSubmitting(true);
      setError(null);
      const attributes = { name: name.trim(), default_amount: amount, enabled };
      if (surcharge) {
        await handleUpdateSurcharge(surcharge.id, attributes);
      } else {
        await handleCreateSurcharge(attributes);
      }
      onClose();
    } catch (e) {
      const apiErrors = e && e.response && e.response.data;
      setError(
        Array.isArray(apiErrors) && apiErrors.length > 0
          ? apiErrors.join(", ")
          : "Could not save this fee. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={false}
      title={surcharge ? "Edit fee" : "Add a fee or surcharge"}
      footer={(
        <>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="primary"
            data-testid="surcharge-submit"
            onClick={submit}
            disabled={submitting || !isValid}
          >
            {submitting ? "Saving…" : surcharge ? "Save" : "Add fee"}
          </Button>
        </>
      )}
    >
      <div data-testid="surcharge-modal">
        {error && (
          <div className="mb-3" data-testid="surcharge-error">
            <Alert color="error" role="alert" title={error} />
          </div>
        )}
        <div className="mb-3">
          <TextInput
            label="Label"
            id="surcharge-name"
            data-testid="surcharge-name"
            maxLength={255}
            placeholder="e.g. Facility Fee"
            hint="Buyers see this as its own line at checkout and on their receipt."
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <TextInput
            label="Amount per pass or pack"
            id="surcharge-amount"
            data-testid="surcharge-amount"
            type="number"
            inputMode="decimal"
            min="0.01"
            step="0.01"
            onWheel={(e) => e.currentTarget.blur()}
            leadingText={fmtCurrencySymbol(series.base_currency_symbol)}
            placeholder="0.00"
            hint="The full amount goes to you. Only card processing is added on top."
            error={amountTouched && amount !== "" && !amountValid ? "Enter an amount above zero in whole cents, e.g. 2.50" : undefined}
            onBlur={() => setAmountTouched(true)}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <Checkbox
          id="surcharge-enabled"
          data-testid="surcharge-enabled"
          label="Charge this fee"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
      </div>
    </Modal>
  );
};

SeriesPassSurchargeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  surcharge: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default SeriesPassSurchargeModal;
